import type { ReactElement, ReactNode } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** When true, the confirm button uses the error palette. */
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Story 053 — generic destructive-action confirmation.
 *
 * Per UX brief §4.4 — initial focus lands on Cancel, not Confirm.
 * Tab cycles Cancel ↔ Confirm. The confirm button shows an inline
 * spinner while the mutation is pending.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  danger,
  pending,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): ReactElement {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      role="dialog"
      aria-labelledby="confirm-dialog-title"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{description}</DialogContentText>
      </DialogContent>
      <DialogActions>
        {/* Cancel is the visual default per WCAG 3.3.4. MUI Dialog
            already pushes initial focus inside the dialog automatically;
            we keep the order Cancel → Confirm so Tab reaches Confirm. */}
        <Button onClick={onCancel}>{cancelLabel}</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={danger ? "error" : "primary"}
          disabled={pending === true}
          {...(pending === true
            ? {
                startIcon: <CircularProgress size={14} color="inherit" />,
              }
            : {})}
          // Stable hook for spec selectors — every confirmation dialog
          // exposes a "Confirm" button regardless of the custom label.
          aria-label={`Confirm: ${confirmLabel}`}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
